/**
 * lib/content-nav — ПЕРЕИСПОЛЬЗУЕМЫЙ модуль меню разделов контент-страниц.
 *
 * ЗАЧЕМ / WHY THIS EXISTS: до этого контент-страницы были осиротевшими — с `/en/`
 * нельзя было попасть на how-to-play/rules/strategy/faq/about (видимого меню разделов
 * не было). Тикет content-pages-conformance 2026-06-17 (AC-8/9/10): меню разделов
 * обязано быть ОТДЕЛЬНЫМ переиспользуемым модулем (модель «ствол→ветки»), который
 * экран только МОНТИРУЕТ, а не верстает список руками под каждый раздел/язык.
 *
 * Что делает: строит nav-разметку всех разделов «Помощи» из ЕДИНОГО источника
 * (`contentNavLinks` из lib/content-routing → shared/content-routes), подсвечивает
 * активный раздел и отдаёт `<a class="content-link">` с data-lang/data-slug —
 * тот же механизм, что крошки/lang-switcher, поэтому делегированный клик экрана
 * (`navigateContent`) перехватывает их БЕЗ дублирования логики навигации.
 *
 * Язык — ПАРАМЕТР (AC-9): один модуль рендерит меню на всех 12 страницах (en+ru × 6),
 * никаких per-language веток. Названия разделов = labels (must-visible §0.B) — не
 * обрезаются ellipsis; размеры — формулами от --m/--fs-* (co-located content-nav.css).
 *
 * SCOPE: построить DOM меню разделов + подсветить активный. NOT IN SCOPE: маршрутная
 *   логика (lib/content-routing — единый ствол), перехват кликов (делегирование в
 *   content-screen), источник списка разделов (contentNavLinks → shared/content-routes).
 *
 * Cross-references: тикет AC-8/9/10; code-structure §1 (ствол→ветки) / §2 (примитивы);
 *   lib/content-routing (contentNavLinks — единый источник навигации); content-nav.css.
 */

import '../styles/content-nav.css';

import { contentNavLinks } from './content-routing.js';
import { contentPath } from '@lines98/shared/config/content-routes';
import type { ContentLang } from '@lines98/shared/config/seo';

export interface ContentNavOptions {
  /** Текущий язык — ПАРАМЕТР (не ветка): один модуль на все языки. */
  readonly lang: ContentLang;
  /** Активный раздел (slug) — подсвечивается ровно один пункт. */
  readonly activeSlug: string;
  /** Доступное имя для landmark (i18n из ui-бандла). */
  readonly ariaLabel: string;
  /**
   * Включать ли пункт лендинга (slug ''=«Главная») в список. По умолчанию true
   * (общее меню/крошки). Оглавление «Помощи» (lib/help-toc) передаёт false —
   * лендинг не раздел «Помощи», а корень сайта (он на бренд-логотипе рейла). Список
   * по-прежнему из ЕДИНОГО источника contentNavLinks — это фильтр, не свой массив.
   */
  readonly includeLanding?: boolean;
}

export interface ContentNavHandle {
  /** Корневой `<nav>` — экран вставляет его в chrome-зону (sticky, не в скролл-тело). */
  readonly element: HTMLElement;
}

/** HTML-escape для текстовых/атрибутных позиций (label/slug/lang). */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Построить меню разделов. Список разделов — ИЗ ЕДИНОГО источника contentNavLinks
 * (не хардкод-массив): с любого раздела достижимы все 6, активный подсвечен.
 * Ссылки — `.content-link` (data-lang/data-slug) → делегированный клик экрана делает
 * SPA-переход через navigateContent (единый механизм навигации, без дубля).
 */
export function createContentNav(opts: ContentNavOptions): ContentNavHandle {
  const all = contentNavLinks(opts.lang, opts.activeSlug);
  // includeLanding=false → отбросить лендинг (slug '') из списка (оглавление «Помощи»);
  // это ФИЛЬТР единого источника, не свой хардкод-массив (AC-9/12).
  const links = opts.includeLanding === false ? all.filter((l) => l.slug !== '') : all;

  const nav = document.createElement('nav');
  nav.className = 'content-nav';
  nav.setAttribute('aria-label', opts.ariaLabel);

  nav.innerHTML = links
    .map((l) => {
      const active = l.active ? ' aria-current="page"' : '';
      const activeClass = l.active ? ' content-nav__item--active' : '';
      // href = origin-relative (per ADR-0020, contentPath); data-* для SPA-перехвата.
      const href = contentPath(opts.lang, l.slug);
      return `<a class="content-link content-nav__item${activeClass}" data-lang="${esc(
        opts.lang,
      )}" data-slug="${esc(l.slug)}" href="${esc(href)}"${active}>${esc(l.label)}</a>`;
    })
    .join('');

  return { element: nav };
}
